/**
 * @fileoverview FAQ Page - Review System & Accumulation Control
 *
 * Explains how the spaced repetition system works, including:
 * - Daily review caps
 * - Smart reset for immature cards
 * - Mature card preservation
 * - Vacation mode threshold
 *
 * @module routes/app/faq
 */

import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronDown, ChevronUp, Settings, Calendar, Brain, Target } from 'lucide-react'
import { useState, useEffect } from 'react'
import { usePostHog } from '@/hooks/usePostHog'

export const Route = createFileRoute('/app/faq')({
  component: FAQPage,
})

interface FAQItem {
  question: string
  answer: string
  icon?: React.ReactNode
  category: 'basics' | 'accumulation' | 'settings' | 'technical'
}

function FAQPage() {
  const { trackEvent } = usePostHog()
  const [expandedId, setExpandedId] = useState<number | null>(0) // First item expanded by default

  // Track FAQ page view on mount
  useEffect(() => {
    trackEvent('faq_viewed')
  }, [trackEvent])

  const faqItems: FAQItem[] = [
    // Basics
    {
      question: 'How does the spaced repetition system work?',
      answer:
        'JingLearn uses the SuperMemo SM-2 algorithm to schedule flashcard reviews. When you review a card, you grade how well you remembered it (0-5). Based on your grade, the system calculates when you should see that card again. Cards you know well appear less frequently, while difficult cards appear more often. This optimizes your learning by reviewing at the exact moment your brain is about to forget.',
      icon: <Brain className="w-5 h-5 text-cyan-400" />,
      category: 'basics',
    },
    {
      question: 'What do the grade buttons (0-5) mean?',
      answer:
        '0 = Complete blackout (no recall)\n1 = Incorrect but familiar\n2 = Incorrect but almost correct\n3 = Correct with difficulty\n4 = Correct with hesitation\n5 = Perfect recall\n\nThe higher your grade, the longer the interval until you see that card again. Use keyboard shortcuts 0-5 for faster reviews!',
      icon: <Target className="w-5 h-5 text-cyan-400" />,
      category: 'basics',
    },

    // Accumulation Control
    {
      question: 'What happens if I miss reviews for several days?',
      answer:
        'JingLearn includes smart "accumulation control" to prevent overwhelming catch-up sessions. By default, you\'ll see a maximum of 50 flashcards per day, even if hundreds are overdue. The system also applies "smart reset" logic: cards you were still learning (less than 3 weeks old) get reset to help you relearn them properly, while well-established cards (3+ weeks interval) preserve your progress.',
      icon: <Calendar className="w-5 h-5 text-cyan-400" />,
      category: 'accumulation',
    },
    {
      question: 'What is the daily review cap?',
      answer:
        'The daily cap limits how many flashcards you review in a single day, preventing overwhelming sessions after absences. The default is 50 cards per day, but you can adjust this from 10-200 in your settings. If you have 150 cards due, you\'ll see 50 today, 50 tomorrow, and 50 the day after - a manageable gradual catch-up.',
      category: 'accumulation',
    },
    {
      question: 'What are "immature" vs "mature" cards?',
      answer:
        'Immature cards are those with review intervals less than 21 days (3 weeks). These are cards you\'re still actively learning. Mature cards have intervals of 21+ days, meaning you\'ve demonstrated long-term retention. This distinction matters for the smart reset feature: immature cards are more likely to be forgotten during absences and benefit from being reset.',
      category: 'accumulation',
    },
    {
      question: 'What is "smart reset" and when does it trigger?',
      answer:
        'Smart reset automatically resets immature cards when you miss reviews for more than 3 consecutive days (configurable in settings). For example, if a card was due 5 days ago and has an interval of 10 days (immature), it will be marked for reset. You\'ll see a "Review as New" indicator, and reviewing it starts the spacing cycle fresh. This prevents reviewing cards you\'ve likely forgotten, which wastes time and creates false confidence.',
      category: 'accumulation',
    },
    {
      question: 'Will I lose progress on cards I know well?',
      answer:
        'No! Mature cards (21+ day intervals) preserve your progress by default. Even if you return after a long absence, cards you\'ve mastered won\'t be reset. However, you can enable "Reset Mature Cards" in settings if you prefer to relearn everything after extended breaks.',
      category: 'accumulation',
    },

    // Settings
    {
      question: 'How do I customize the accumulation control settings?',
      answer:
        'Visit Settings to configure:\n• Daily Review Cap (10-200 cards, default 50)\n• Vacation Mode Threshold (1-14 days, default 3)\n• Reset Immature Cards (default ON)\n• Reset Mature Cards (default OFF)\n\nThese settings take effect immediately for your next review session.',
      icon: <Settings className="w-5 h-5 text-cyan-400" />,
      category: 'settings',
    },
    {
      question: 'What is the "vacation mode threshold"?',
      answer:
        'This is the number of consecutive missed days that triggers smart reset for immature cards. The default is 3 days, allowing weekend breaks without penalty. If you increase it to 7 days, cards won\'t be reset until you\'ve missed a full week. Lower values (1-2 days) make the system more strict, resetting cards sooner.',
      category: 'settings',
    },

    // Technical
    {
      question: 'How are review intervals calculated?',
      answer:
        'JingLearn uses the SuperMemo SM-2 algorithm:\n1. Your grade (0-5) adjusts the "ease factor" (difficulty multiplier)\n2. The ease factor determines how much the interval grows\n3. First review: 1 day\n4. Second review: 6 days\n5. Subsequent reviews: previous interval × ease factor\n\nA grade of 4+ grows intervals exponentially (1d → 6d → 15d → 37d...). Grades below 3 reset the card to the beginning.',
      category: 'technical',
    },
    {
      question: 'Why do some cards appear in "song review mode"?',
      answer:
        'JingLearn groups flashcards by their source song, allowing you to review cards in the context of the music. This leverages auditory memory and makes reviews more engaging. You can hear the exact moment in the song where each phrase appears, reinforcing the connection between sound and meaning.',
      category: 'technical',
    },
    {
      question: 'What happens to cards marked "Review as New"?',
      answer:
        'Cards flagged by smart reset show a "Review as New" indicator. When you review them, the system treats your grade as if the card is being learned for the first time, restarting the spacing intervals. This ensures cards you\'ve forgotten get proper re-learning rather than premature spacing.',
      category: 'technical',
    },
  ]

  const categories = {
    basics: 'Getting Started',
    accumulation: 'Accumulation Control',
    settings: 'Settings & Customization',
    technical: 'Technical Details',
  }

  const toggleExpand = (index: number) => {
    const isExpanding = expandedId !== index
    setExpandedId(expandedId === index ? null : index)

    // Track FAQ item expansion
    if (isExpanding) {
      const item = faqItems[index]
      trackEvent('faq_item_expanded', {
        faqQuestion: item.question,
        faqCategory: item.category,
      })
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-cyan-400 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-cyan-400 font-mono mb-2">
            [ FREQUENTLY ASKED QUESTIONS ]
          </h1>
          <p className="text-gray-400">
            Learn how JingLearn's spaced repetition and accumulation control work
          </p>
          <div className="mt-4 flex gap-4">
            <Link
              to="/app/settings"
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400 text-cyan-400 hover:bg-cyan-400/20 transition-colors rounded font-mono text-sm"
            >
              <Settings className="w-4 h-4" />
              Configure Settings
            </Link>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {Object.entries(categories).map(([categoryKey, categoryLabel]) => {
          const categoryItems = faqItems.filter((item) => item.category === categoryKey)

          return (
            <div key={categoryKey} className="mb-12">
              <h2 className="text-xl font-bold text-green-400 font-mono mb-6 border-b border-gray-700 pb-2">
                {'>'} {categoryLabel}
              </h2>

              <div className="space-y-4">
                {categoryItems.map((item) => {
                  const globalIndex = faqItems.indexOf(item)
                  const isExpanded = expandedId === globalIndex

                  return (
                    <div
                      key={globalIndex}
                      className="border border-gray-700 bg-slate-900/30 rounded overflow-hidden hover:border-cyan-400/50 transition-colors"
                    >
                      <button
                        onClick={() => toggleExpand(globalIndex)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-900/50 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {item.icon}
                          <span className="text-lg font-medium text-white">{item.question}</span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="px-6 py-4 border-t border-gray-700 bg-black/30">
                          <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                            {item.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}

        {/* Additional Help */}
        <div className="mt-12 p-6 border border-green-400 bg-green-400/5 rounded">
          <h3 className="text-lg font-bold text-green-400 font-mono mb-3">
            [ NEED MORE HELP? ]
          </h3>
          <p className="text-gray-300 mb-4">
            Still have questions about the review system or accumulation control? Here are some
            helpful resources:
          </p>
          <ul className="space-y-2 text-gray-400">
            <li className="flex items-center gap-2">
              <span className="text-cyan-400">•</span>
              <Link to="/app/settings" className="text-cyan-400 hover:underline">
                Review your current settings
              </Link>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-cyan-400">•</span>
              <span>
                Read the{' '}
                <a
                  href="https://en.wikipedia.org/wiki/SuperMemo#SM-2_algorithm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:underline"
                >
                  SuperMemo SM-2 documentation
                </a>
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-cyan-400">•</span>
              <span>Check the accumulation control design document (developers)</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
